import { useQuery } from "@tanstack/react-query";
import { addDays, addHours, addMonths, startOfDay, subMonths } from "date-fns";
import { type FormEvent, useMemo, useState } from "react";
import { api, queryString } from "../../api/client";
import type { EventRecord, ListResponse } from "../../api/types";
import { useTenantCurrency } from "../../app/tenant";
import {
  formatDateTime,
  localInputValue,
  titleCase,
  zonedDateTimeInput,
  zonedInputToISO,
} from "../../lib/format";
import { ErrorPanel, Field, FormActions, Notice } from "../../ui/components";
import { SkeletonFilters } from "../../ui/skeletons";
import type { CalendarResponse } from "../bookings/calendarTypes";
import { ContactPicker } from "../contacts/ContactPicker";
import { useAllLocations, useVenues } from "../venues/queries";
import { EVENT_CREATE_STATUSES } from "./eventLifecycle";

// EventPayload is the body both the conflict check and the write accept, so what was measured for
// clashes is exactly what gets saved. Building a second, slightly different object for the write
// is how a schedule ends up approved on one shape and stored as another.
export interface EventPayload {
  title: string;
  summary: string;
  status?: string;
  bookingId: string;
  customerId: string;
  start: string;
  end: string;
  timezone: string;
  setupMinutes: number;
  teardownMinutes: number;
  attendeeCount: number | null;
  budgetMinor: number;
  internalNotes: string;
  ownerUserId: string;
  locationIds: string[];
  version?: number;
}

interface WorkspaceMember {
  userId: string;
  displayName: string;
  membershipStatus: string;
}

export function EventForm({
  mode,
  initial,
  defaultStart,
  submitting,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  mode: "create" | "edit";
  initial?: EventRecord | undefined;
  // The day somebody picked on the calendar, when they arrived from a square rather than from the
  // events list. Without it the plus sign on a Tuesday opened a form dated tomorrow, and the form
  // quietly disagreed with the click that produced it.
  defaultStart?: Date | undefined;
  submitting: boolean;
  submitLabel: string;
  onCancel(): void;
  onSubmit(payload: EventPayload): void;
}) {
  const venuesQuery = useVenues();
  const venues = venuesQuery.data?.items ?? [];
  const locationsQuery = useAllLocations(venues);
  const currency = useTenantCurrency();
  const membersQuery = useQuery({
    queryKey: ["tenant", "members", "event-owner"],
    queryFn: () => api<ListResponse<WorkspaceMember>>("/api/v1/members"),
  });
  // A window rather than every booking ever taken: the tie is nearly always to something in the
  // current season, and the calendar endpoint refuses ranges beyond a year anyway.
  const window = useMemo(() => {
    const from = subMonths(startOfDay(new Date()), 2);
    return { from: from.toISOString(), to: addMonths(from, 10).toISOString() };
  }, []);
  const bookingsQuery = useQuery({
    queryKey: ["bookings", "event-tie-picker", window.from, window.to],
    queryFn: () =>
      api<CalendarResponse>(
        `/api/v1/calendar${queryString({ from: window.from, to: window.to, kind: "booking" })}`,
      ),
  });

  const [tie, setTie] = useState<"own" | "booking">(initial?.bookingId ? "booking" : "own");
  const [bookingId, setBookingId] = useState(initial?.bookingId ?? "");
  const [locationIds, setLocationIds] = useState<string[]>(() =>
    initial && initial.spaceSource === "own"
      ? [...new Set(initial.allocations.map((allocation) => allocation.locationId))]
      : [],
  );

  const activeLocations = locationsQuery.locations.filter(
    (location) => location.isBookable !== false && location.status === "active",
  );
  // One row per booking. The calendar answers in occurrences, so a weekly class would otherwise
  // offer the same booking to tie to thirteen times.
  const tieOptions = useMemo(() => {
    const found = new Map<string, { id: string; label: string; timezone: string }>();
    for (const entry of bookingsQuery.data?.items ?? []) {
      if (entry.kind === "event" || found.has(entry.bookingId)) continue;
      found.set(entry.bookingId, {
        id: entry.bookingId,
        label: `${entry.reference} · ${entry.title}${entry.customerName ? ` · ${entry.customerName}` : ""}`,
        timezone: entry.timezone,
      });
    }
    // An event already tied to a booking outside the window keeps its own option, so opening it to
    // change the title does not silently untie it.
    if (initial?.bookingId && !found.has(initial.bookingId)) {
      found.set(initial.bookingId, {
        id: initial.bookingId,
        label: `${initial.bookingReference} · ${initial.bookingTitle}`,
        timezone: initial.timezone,
      });
    }
    return [...found.values()];
  }, [bookingsQuery.data, initial]);

  const fallbackTimezone =
    initial?.timezone ||
    venues[0]?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    "UTC";
  // Times are entered as they are read out at the venue, so the zone is taken from whatever now
  // holds the space rather than from the browser of whoever happens to be typing.
  const timezone = useMemo(() => {
    if (tie === "booking") {
      return tieOptions.find((option) => option.id === bookingId)?.timezone || fallbackTimezone;
    }
    const first = activeLocations.find((location) => locationIds.includes(location.id));
    if (!first) return fallbackTimezone;
    return (
      first.timezone ||
      venues.find((venue) => venue.id === first.venueId)?.timezone ||
      fallbackTimezone
    );
  }, [tie, bookingId, tieOptions, activeLocations, locationIds, venues, fallbackTimezone]);

  const startSeed = defaultStart ?? startOfDay(addDays(new Date(), 1));
  const startDefault = initial
    ? zonedDateTimeInput(initial.start, initial.timezone)
    : localInputValue(startSeed);
  const endDefault = initial
    ? zonedDateTimeInput(initial.end, initial.timezone)
    : localInputValue(addHours(startSeed, 3));

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const attendees = String(data.get("attendeeCount") ?? "").trim();
    const budget = String(data.get("budget") ?? "").trim();
    const payload: EventPayload = {
      title: String(data.get("title") ?? ""),
      summary: String(data.get("summary") ?? ""),
      bookingId: tie === "booking" ? bookingId : "",
      customerId: String(data.get("customerId") ?? ""),
      start: zonedInputToISO(String(data.get("start") ?? ""), timezone),
      end: zonedInputToISO(String(data.get("end") ?? ""), timezone),
      timezone,
      setupMinutes: Number(data.get("setupMinutes") ?? 0),
      teardownMinutes: Number(data.get("teardownMinutes") ?? 0),
      attendeeCount: attendees === "" ? null : Number(attendees),
      budgetMinor: budget === "" ? 0 : Math.round(Number(budget) * 100),
      internalNotes: String(data.get("internalNotes") ?? ""),
      ownerUserId: String(data.get("ownerUserId") ?? ""),
      // A tied event holds no space of its own: the booking underneath it already blocked those
      // rooms, and a second set of reservations would be found clashing with the very booking the
      // event belongs to.
      locationIds: tie === "booking" ? [] : locationIds,
    };
    if (mode === "create") payload.status = String(data.get("status") ?? "planning");
    else payload.version = initial?.version ?? 0;
    onSubmit(payload);
  }

  if (venuesQuery.isLoading || locationsQuery.loading) return <SkeletonFilters bare count={8} />;

  return (
    <form className="form-stack" onSubmit={submit}>
      {venuesQuery.error || locationsQuery.error || bookingsQuery.error ? (
        <ErrorPanel error={venuesQuery.error ?? locationsQuery.error ?? bookingsQuery.error} />
      ) : null}
      <div className="form-grid">
        <Field label="Event title">
          <input name="title" required maxLength={240} defaultValue={initial?.title ?? ""} />
        </Field>
        {mode === "create" ? (
          <Field
            label="Initial status"
            hint="Later states are reached by a transition that records why."
          >
            <select name="status" defaultValue="planning">
              {EVENT_CREATE_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {titleCase(value)}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field
          label="Space"
          hint="An event either takes rooms of its own or runs inside a booking somebody made."
        >
          <select
            name="spaceSource"
            value={tie}
            onChange={(event) => setTie(event.target.value as "own" | "booking")}
          >
            <option value="own">We hold the space for this event</option>
            <option value="booking">Delivered under an existing booking</option>
          </select>
        </Field>
        {tie === "booking" ? (
          <Field
            label="Booking"
            hint="The rooms, the customer and the money stay on that booking. The event lays its program over it and keeps the calendar truthful."
          >
            <select
              name="bookingId"
              required
              value={bookingId}
              onChange={(event) => setBookingId(event.target.value)}
            >
              <option value="" disabled>
                {bookingsQuery.isLoading ? "Loading bookings" : "Select a booking"}
              </option>
              {tieOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label={`Starts (${timezone})`}>
          <input name="start" type="datetime-local" required defaultValue={startDefault} />
        </Field>
        <Field label={`Ends (${timezone})`}>
          <input name="end" type="datetime-local" required defaultValue={endDefault} />
        </Field>
        <Field label="Setup minutes" hint="Hours the room is held before doors open.">
          <input
            name="setupMinutes"
            type="number"
            min={0}
            max={10080}
            defaultValue={initial?.setupMinutes ?? 0}
          />
        </Field>
        <Field label="Teardown minutes">
          <input
            name="teardownMinutes"
            type="number"
            min={0}
            max={10080}
            defaultValue={initial?.teardownMinutes ?? 0}
          />
        </Field>
        <Field label="Expected attendance">
          <input
            name="attendeeCount"
            type="number"
            min={0}
            defaultValue={initial?.attendeeCount ?? ""}
          />
        </Field>
        <Field
          label={`Planned budget (${currency})`}
          hint="Optional. What the client plans to spend running this event. Sponsor cash tops it up on the money card."
        >
          <input
            name="budget"
            type="number"
            min={0}
            step={0.01}
            defaultValue={initial?.budgetMinor ? (initial.budgetMinor / 100).toFixed(2) : ""}
          />
        </Field>
        <Field label="Event lead" hint="Who is answerable for this event running.">
          <select name="ownerUserId" defaultValue={initial?.ownerId ?? ""}>
            <option value="">Not assigned yet</option>
            {(membersQuery.data?.items ?? [])
              .filter((member) => member.membershipStatus === "active")
              .map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.displayName}
                </option>
              ))}
          </select>
        </Field>
        <Field
          label="Partner or co-host"
          hint="Optional. An internal event usually has nobody outside the organization to write to."
        >
          <ContactPicker
            name="customerId"
            defaultContactId={initial?.customerId}
            placeholder="Nobody outside the organization"
          />
        </Field>
        <Field label="What is happening">
          <textarea name="summary" maxLength={10000} defaultValue={initial?.summary ?? ""} />
        </Field>
        <Field label="Internal notes">
          <textarea
            name="internalNotes"
            maxLength={20000}
            defaultValue={initial?.internalNotes ?? ""}
          />
        </Field>
        {tie === "own" ? (
          <fieldset className="field full">
            <legend>Rooms</legend>
            <div className="choice-grid">
              {venues.map((venue) => (
                <div className="choice-group" key={venue.id}>
                  <strong>{venue.name}</strong>
                  {activeLocations
                    .filter((location) => location.venueId === venue.id)
                    .map((location) => (
                      <label className="check-field" key={location.id}>
                        <input
                          type="checkbox"
                          name="locationIds"
                          value={location.id}
                          checked={locationIds.includes(location.id)}
                          onChange={(event) =>
                            setLocationIds((current) =>
                              event.target.checked
                                ? [...current, location.id]
                                : current.filter((value) => value !== location.id),
                            )
                          }
                        />
                        <span>{location.name}</span>
                      </label>
                    ))}
                </div>
              ))}
            </div>
          </fieldset>
        ) : null}
      </div>
      {tie === "own" && locationIds.length === 0 ? (
        <Notice tone="info">
          No room is held yet. The event can be planned first and given a room later, and nothing is
          blocked out in the meantime.
        </Notice>
      ) : null}
      {initial ? (
        <p className="muted">Last saved {formatDateTime(initial.updatedAt, initial.timezone)}.</p>
      ) : null}
      <FormActions submitting={submitting} submitLabel={submitLabel} onCancel={onCancel} />
    </form>
  );
}
